import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import { formatCurrency } from '@eriko/shared-types';
import {
  AlertTriangle,
  ArrowUpRight,
  BookOpen,
  Receipt,
} from 'lucide-react';
import { Link } from 'react-router';

export function MonthlySummaryView({ data }: { data: Record<string, unknown> }) {
  const totalRevenue = Number(data.totalRevenue ?? 0);
  const totalExpenses = Number(data.totalExpenses ?? 0);
  const netProfit = Number(data.netProfit ?? 0);
  const transactionCount = Number(data.transactionCount ?? 0);
  const flagCount = Number(data.flagCount ?? 0);
  const monthlyCloseId = data.monthlyCloseId as string | undefined;
  const topExpenseCategories =
    (data.topExpenseCategories as { name: string; amount: number }[]) ?? [];
  const year = data.year;
  const month = data.month;

  const monthLabel =
    year && month
      ? new Date(Number(year), Number(month) - 1).toLocaleString('en-US', {
          month: 'long',
          year: 'numeric',
        })
      : 'Monthly Summary';

  const isProfit = netProfit >= 0;
  const profitMargin = totalRevenue > 0
    ? Math.round((netProfit / totalRevenue) * 100)
    : 0;
  const revenueRatio = totalRevenue + totalExpenses > 0
    ? (totalRevenue / (totalRevenue + totalExpenses)) * 100
    : 50;
  const maxExpense = topExpenseCategories.length > 0
    ? Math.max(...topExpenseCategories.map((c) => c.amount))
    : 0;

  return (
    <div>
      {/* Month header */}
      <div className="flex items-baseline justify-between mb-8">
        <h3 className="font-serif text-xl text-surface-800">
          {monthLabel}
        </h3>
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary" className="gap-1 text-[0.625rem] px-1.5 py-0">
            <Receipt className="h-2.5 w-2.5" />
            {transactionCount}
          </Badge>
          {flagCount > 0 && (
            <Badge variant="destructive" className="gap-1 text-[0.625rem] px-1.5 py-0">
              <AlertTriangle className="h-2.5 w-2.5" />
              {flagCount}
            </Badge>
          )}
        </div>
      </div>

      {/* Net profit — hero */}
      <div className="text-center mb-2">
        <p className="text-[0.625rem] font-semibold uppercase tracking-widest text-surface-400 mb-1.5">
          Net Profit
        </p>
        <p className={`text-4xl font-serif tracking-tight ${isProfit ? 'text-pos' : 'text-neg'}`}>
          {formatCurrency(netProfit)}
        </p>
      </div>

      {/* Margin indicator */}
      <div className="flex items-center justify-center gap-1.5 mb-8">
        <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${isProfit ? 'text-pos' : 'text-neg'}`}>
          {isProfit && <ArrowUpRight className="h-3 w-3" />}
          {profitMargin}% margin
        </span>
      </div>

      {/* Revenue vs Expenses proportion bar */}
      <div className="mb-6">
        <div className="flex h-2 rounded-full overflow-hidden">
          <div
            className="bg-pos transition-all duration-500 rounded-l-full"
            style={{ width: `${revenueRatio}%` }}
          />
          <div
            className="bg-neg/60 transition-all duration-500 rounded-r-full"
            style={{ width: `${100 - revenueRatio}%` }}
          />
        </div>
        <div className="flex justify-between mt-2.5">
          <div>
            <p className="text-[0.625rem] font-medium uppercase tracking-wider text-surface-400">Revenue</p>
            <p className="text-sm font-medium font-mono text-pos">{formatCurrency(totalRevenue)}</p>
          </div>
          <div className="text-right">
            <p className="text-[0.625rem] font-medium uppercase tracking-wider text-surface-400">Expenses</p>
            <p className="text-sm font-medium font-mono text-neg">{formatCurrency(totalExpenses)}</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-surface-200 my-6" />

      {/* Top expense categories with proportion bars */}
      {topExpenseCategories.length > 0 && (
        <div className="mb-6">
          <p className="text-[0.625rem] font-semibold uppercase tracking-widest text-surface-400 mb-3">
            Top Expenses
          </p>
          <div className="space-y-2.5">
            {topExpenseCategories.map((cat) => {
              const pct = maxExpense > 0 ? (cat.amount / maxExpense) * 100 : 0;
              return (
                <div key={cat.name}>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-xs text-surface-600 truncate mr-3">{cat.name}</span>
                    <span className="text-xs font-mono font-medium text-surface-800 tabular-nums shrink-0">
                      {formatCurrency(cat.amount)}
                    </span>
                  </div>
                  <div className="h-1 rounded-full bg-surface-100">
                    <div
                      className="h-full rounded-full bg-surface-400 transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* View Books link */}
      {monthlyCloseId && (
        <Button variant="outline" size="sm" className="w-full gap-2" asChild>
          <Link to={`/books/${monthlyCloseId}`}>
            <BookOpen className="h-3.5 w-3.5" />
            View Books
          </Link>
        </Button>
      )}
    </div>
  );
}
